// Declared in SharedModule next to LocationPickerComponent, like its hosts.
/* eslint-disable @angular-eslint/prefer-standalone */
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { MapFeatureLink, MapFeatureLinkCapture } from '../../models/map-feature-link';
import {
  captureNeedsRetake, captureViewerAttachment, designContextStripLines, linkCaptureImage, revisionBadgeText, savedMarkupKind,
} from '../../helpers/design-context';
import { DesignContextAvailabilityService } from '../../services/design-context-availability.service';
import { ProjectMapManifestService } from '../../services/project-map-manifest.service';
import { MarkupService } from '../../services/markup.service';
import { AppService } from '../../services/app.service';
import { Markup } from '../../models/markup';
import { MarkupEditorConfig, MarkupSaveResult } from '../markup-editor/markup-editor.types';
import { ProjectsService } from '../../../modules/projects/core/projects.service';
import { HeldSheetMarkup, heldMarkup, newClientKey, SheetMarkupQueueService } from '../../services/sheet-markup-queue.service';
import { AnalyticsService } from '../../services/analytics.service';
import { SheetPreviewService } from '../../services/sheet-preview.service';
import { CropBox, SHEET_PREVIEW_ZOOM, SheetPreviewCrop, sheetPreviewCrop } from '../../helpers/sheet-preview-crop';
import { AS_BUILT_STATUS_COPY, markupBBox } from '../../../modules/projects/pages/projects-details-page/components/tab-screens/drawings/drawing-as-built';
import { SheetFocusService } from '../../services/sheet-focus.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../confirm-dialog/confirm-dialog.component';
import { confirmLinkRemoval, PINLESS_EMPTY_CONFIRM } from '../../helpers/link-removal';

/**
 * POST …/map-feature-links/:uuid/snapshot refusals (api); anything else (offline, 5xx) gets the generic line.
 * Same strings as mobile's `RETRY_SNAPSHOT_MESSAGES` (src/utils/designContext.js).
 */
const RETRY_ERRORS: Record<number, string> = {
  403: 'Only a project manager or lead inspector can retry snapshots.',
  404: 'This design context no longer exists.',
  409: 'A snapshot is already being rendered.',
  410: 'The linked layer version was removed; no snapshot can be rendered.',
  503: 'Snapshot rendering isn’t set up on this server.',
};

/**
 * A saved link's pending snapshot is re-read at once, then every POLL_INTERVAL_MS, for at most POLL_MAX_CHECKS
 * requests (~60 s of visible time; the worker renders in seconds). Like mobile, checks are counted, not wall-clock.
 */
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_CHECKS = 20;

/**
 * One record's design context, laid out like the picker's linked-feature strip (c0086de5, mobile 90912be2): a
 * "LINKED FEATURE" label row with the "…" (Edit location, Mark up, Remove link), then an optional 60px tile and the
 * text lines (title; layer · offset; muted hints for the revision state, a moved capture and the snapshot state).
 * Where the host's location slot shows the capture (`collapseWhenCaptured`, spec addendum §11) there is no tile and
 * no snapshot state. Hosted by the bid-item / location-station editors, and read-only on summary cards.
 */
@Component({
  selector: 'app-design-context',
  templateUrl: './design-context.component.html',
  styleUrls: ['./design-context.component.scss'],
  standalone: false,
})
export class DesignContextComponent implements OnChanges, OnDestroy {
  private availability = inject(DesignContextAvailabilityService);
  private manifestApi = inject(ProjectMapManifestService);
  private cdr = inject(ChangeDetectorRef);
  private markupService = inject(MarkupService);
  private appService = inject(AppService);
  private router = inject(Router);
  private projectsService = inject(ProjectsService);
  private sheetPreviews = inject(SheetPreviewService);
  private injector = inject(Injector);
  private sheetFocus = inject(SheetFocusService);
  private dialog = inject(MatDialog);

  @Input() link: MapFeatureLink | null = null;
  @Input() editable = false;
  /** For the revision badge and snapshot retry. */
  @Input() projectId: string | null = null;
  /**
   * Set by hosts that show the capture in their location slot (app-design-capture-preview): there the card drops its
   * tile and every snapshot state (spec addendum §11). Elsewhere (summaries with no minimap) the tile shows the capture.
   */
  @Input() collapseWhenCaptured = false;
  /** 7.2: the record's markup starts as this kind (As-built for an issue in an as-built category); default note */
  @Input() markupKind: 'note' | 'as_built' | null = null;

  /** Host sets its control to null (the pin stays). */
  @Output() remove = new EventEmitter<void>();
  /**
   * "Edit location" in the "…" (Sri 2026-09-28, mobile ddfc349d): the location slot's click now opens the capture in the
   * viewer, so hosts that show the slot re-open the picker from here. Offered only when the host listens.
   */
  @Output() editLocation = new EventEmitter<void>();
  /**
   * After a successful retry (`snapshot.status: 'pending'`), a saved markup (the new snapshot images), or a
   * pending snapshot that finished rendering (ready/failed). Read-only cards don't bind it; the control shows it anyway.
   */
  @Output() linkChange = new EventEmitter<MapFeatureLink>();

  badge: string | null = null;
  /** The sheet link's client preview: the page render and the record's own markup, once looked up. */
  sheetPreview: { src: string; markupSrc: string | null; strokes?: CropBox | null } | null = null;
  /** The page's width / height, known once its image has loaded; the crop waits for it. */
  sheetPageAspect: number | null = null;
  retrying = false;
  retryError = '';
  /** The existing markup editor, shown inline (it renders itself as a full-screen overlay, like the carousel's). */
  showMarkupEditor = false;
  /** W71: the sheet page the editor is open on (a sheet link's own markup), else the map snapshot or capture */
  private sheetEditor: { revisionUuid: string; attachmentUuid: string; pageNumber: number } | null = null;
  markupConfig: MarkupEditorConfig | null = null;

  private polling = false;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  /** The in-flight request; also the generation token a late answer is checked against. */
  private pollAbort: AbortController | null = null;
  private pollChecks = 0;
  private pollUuid: string | null = null;
  private pollProjectId: string | null = null;
  private waitingForVisible = false;
  /**
   * The capture uuid (or null) the server last sent for the saved link `baselineLinkUuid`: set when the card first
   * sees that link and when a settled poll answer is merged (the same step, never on a pending answer: a pending
   * answer carrying another device's capture would otherwise mask it in the settled merge). A local capture that
   * differs was changed on this page since.
   */
  private baselineLinkUuid: string | null = null;
  private captureBaseline: string | null = null;
  private readonly onVisibilityChange = (): void => {
    if (document.hidden || !this.polling || !this.waitingForVisible || this.pollAbort) return;
    this.waitingForVisible = false;
    void this.pollOnce();
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.link) this.retryError = '';
    if (changes.link && this.link?.uuid && this.link.uuid !== this.baselineLinkUuid) {
      this.baselineLinkUuid = this.link.uuid;
      this.captureBaseline = captureUuid(this.link.capture);
    }
    if (changes.link || changes.projectId) {
      void this.resolveBadge();
      void this.loadSheetPreview();
      // The host writing back the link this control just emitted (Retry, a refresh) is not a new link.
      if (!this.isPollingCurrentLink()) this.startPolling();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /** The picker strip's two lines: the feature label, then layer · offset. */
  get lines(): { title: string; detail: string } { return this.link ? designContextStripLines(this.link) : { title: '', detail: '' }; }
  /** The capture pictured another pin: a muted line under the capture tile (the location slot says it itself). */
  get mapMoved(): boolean { return captureNeedsRetake(this.link); }
  get isSaved(): boolean { return !!this.link?.uuid; }
  /** A drawing sheet link (slice 3): its snapshot is the sheet crop with the pin and the record's redlines. */
  get isSheetLink(): boolean { return !!this.link?.sheet; }
  /** A sheet-only link's snapshot is the sheet crop; a sheet + feature link's is the map snapshot (and can be marked up). */
  get isSheetCrop(): boolean { return this.isSheetLink && !this.link.map_feature_ref; }
  /** Both variants of a sheet link draw the client preview: the card's picture (sheet-only) or a small one under the map snapshot. */
  get showSheetPreview(): boolean { return this.canOpenSheet && !!this.sheetPreview; }
  /** The preview's box shape (width / height), matching `.design-context-sheet-preview` in the stylesheet. */
  get sheetPreviewBoxAspect(): number { return this.isSheetCrop ? 2 : 3; }
  /** Null until the page image has loaded (its aspect is needed to clamp the crop). */
  get sheetCrop(): SheetPreviewCrop | null {
    const point = this.link?.sheet?.point ?? null;
    // Audit 3: fitted to the pin plus the record's strokes, when they reach past today's crop; with no pin (markup only)
    // fitted to the strokes alone, or the whole page
    return this.sheetPageAspect
      ? sheetPreviewCrop(point, this.sheetPageAspect, this.sheetPreviewBoxAspect, SHEET_PREVIEW_ZOOM, this.sheetPreview?.strokes ?? null)
      : null;
  }
  /** Names the sheet and revision for assistive tech, e.g. "C-113 Rev A, location of this record". */
  get sheetPreviewLabel(): string {
    const sheet = this.link?.sheet;
    const name = [sheet?.sheet_number, sheet?.label ? `Rev ${sheet.label}` : null].filter(Boolean).join(' ') || 'Sheet';
    return `${name}, location of this record`;
  }
  get canOpenSheet(): boolean { return this.isSheetLink && !!this.projectId && !!this.link.sheet.revision_uuid; }
  get snapshotStatus(): string { return this.link?.snapshot?.status || 'none'; }
  get snapshotSrc(): string | null { return this.link?.snapshot?.thumb_url || this.link?.snapshot?.preview_url || null; }
  /** The map snapshot only: a sheet crop is never marked up (the record's strokes live on the sheet page). */
  /**
   * W71: a sheet link's own markup (its tagged row on the page), straight from the card: an editable card whose sheet
   * can be opened. The map snapshot's Mark up stays for a sheet + feature link.
   */
  get canMarkUpSheet(): boolean {
    return this.isSheetCrop && this.editable && this.canOpenSheet;
  }

  readonly asBuiltStatusCopy = AS_BUILT_STATUS_COPY;
  /** 7.2: the record's markup on the sheet is an as-built mark: its review status, for the chip */
  get asBuiltStatus(): 'open' | 'reviewed' | 'rejected' | 'incorporated' | null {
    const sheet = this.link?.sheet;
    return sheet?.markup_kind === 'as_built' ? sheet.review_status ?? 'open' : null;
  }

  /** "Edit markup" once the link has strokes: saved on its row, or held until the record saves */
  get sheetHasMarkup(): boolean {
    return !!this.link?.snapshot?.has_markup || !!this.sheetPreview?.markupSrc || (!this.link?.uuid && !!this.link && !!this.heldStrokes(this.link));
  }

  get canMarkUp(): boolean {
    return !this.isSheetCrop && !this.hasCapture && this.editable && this.snapshotStatus === 'ready' && !!this.link?.snapshot?.attachment_uuid;
  }
  get canRetry(): boolean {
    return !this.hasCapture && this.editable && this.isSaved && !!this.projectId
      && (this.snapshotStatus === 'failed' || this.snapshotStatus === 'none');
  }
  /** Mobile's `snapshotText`: an editable saved link with no snapshot that can't be rendered from here (no projectId). */
  get showNoSnapshotYet(): boolean {
    return !this.hasCapture && this.editable && this.isSaved && this.snapshotStatus === 'none' && !this.canRetry;
  }
  /**
   * A capture with a usable image (`linkCaptureImage`, the same test the location slot uses) hides every snapshot
   * state and action: the card shows the capture in its tile, or no tile where the host's location slot shows
   * it (`collapseWhenCaptured`, spec addendum §11). A bare uuid string, an empty object or a capture without urls keeps
   * the snapshot. The poll still follows a pending snapshot; it just stays hidden while this is true.
   */
  get hasCapture(): boolean { return !!linkCaptureImage(this.link); }
  /** No tile and no snapshot state (spec addendum §11) only where the host's location slot shows the capture. */
  get collapsed(): boolean { return this.hasCapture && this.collapseWhenCaptured; }

  private async resolveBadge(): Promise<void> {
    const link = this.link;
    const projectId = this.projectId;
    // "Linked to rev N" (or "Layer withdrawn") until the current revision is known; null for a current, live link.
    this.badge = link ? revisionBadgeText(link, null) : null;
    if (!this.badge || link.withdrawn || link.sheet || !projectId) return;
    try {
      const current = await this.availability.catalogRevision(projectId);
      if (this.link !== link || this.projectId !== projectId) return;
      this.badge = revisionBadgeText(link, current);
    } catch { /* keep "Linked to rev N" */ }
    this.cdr.markForCheck();
  }

  private async loadSheetPreview(): Promise<void> {
    const link = this.link;
    const projectId = this.projectId;
    const revisionUuid = link?.sheet?.revision_uuid;
    this.sheetPreview = null;
    this.sheetPageAspect = null;
    if (!revisionUuid || !projectId) return;
    try {
      const page = await this.sheetPreviews.page(projectId, revisionUuid);
      if (!page || this.link !== link || this.projectId !== projectId) return;
      // The page render is drawn at once; the record's markup joins it when it arrives (a failure keeps the plain page).
      this.sheetPreview = { src: page.previewUrl, markupSrc: null };
      this.cdr.markForCheck();
      // W51: an unsaved link's strokes are held until the record saves; their strokes-only PNG stands in meanwhile
      const layer = link.uuid ? await this.sheetPreviews.linkMarkupLayer(page, link.uuid).catch(() => null) : this.heldLayer(link);
      if (layer?.previewUrl && this.link === link && this.sheetPreview) {
        this.sheetPreview = { ...this.sheetPreview, markupSrc: layer.previewUrl, strokes: layer.box };
        this.cdr.markForCheck();
      }
    } catch { /* no preview: the card keeps its text and Open sheet */ }
  }

  /** W77: Remove link asks first when markup would go with it (held strokes, the record's sheet markup, a marked-up capture) */
  requestRemove(): void {
    const link = this.link;
    if (!link) return;
    confirmLinkRemoval(this.dialog, link, !link.uuid && !!this.heldStrokes(link)).subscribe(go => {
      if (go) this.remove.emit();
    });
  }

  /** The strokes the picker holds for this unsaved sheet link (W51); looked up only when needed, never required */
  private heldStrokes(link: MapFeatureLink): string | null {
    return this.heldLayer(link)?.previewUrl ?? null;
  }

  /** Those held strokes' preview and the box they cover (audit 3: the crop fits to it) */
  private heldLayer(link: MapFeatureLink): { previewUrl: string; box: CropBox | null } | null {
    const sheet = link.sheet;
    if (!sheet?.revision_uuid) return null;
    try {
      const held = this.injector.get(SheetMarkupQueueService).heldFor(link);
      return held?.previewDataUrl
        ? { previewUrl: held.previewDataUrl, box: markupBBox(held.body?.annotation_data, held.body?.canvas_width, held.body?.canvas_height) }
        : null;
    } catch {
      return null;
    }
  }

  onSheetPageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img?.naturalWidth > 0 && img.naturalHeight > 0) {
      this.sheetPageAspect = img.naturalWidth / img.naturalHeight;
      this.cdr.markForCheck();
    }
  }

  async retry(): Promise<void> {
    if (!this.canRetry || this.retrying || !this.projectId) return;
    const link = this.link;
    this.retrying = true;
    this.retryError = '';
    try {
      const updated = await this.manifestApi.retrySnapshot(this.projectId, link.uuid);
      if (this.link !== link) return;
      this.link = updated;
      this.linkChange.emit(updated);
      this.startPolling();
    } catch (error) {
      // A quiet line, never a throw.
      if (this.link === link) this.retryError = RETRY_ERRORS[error?.status] || 'Retry failed. Try again later.';
    } finally {
      this.retrying = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * (Re)start polling a saved link whose snapshot is rendering; stops any earlier poll first.
   * Drafts (no uuid) have nothing to read back; a moved, unsaved pin is caught by the pin check in pollOnce.
   */
  private startPolling(): void {
    this.stopPolling();
    if (!this.link?.uuid || !this.projectId || this.link.snapshot?.status !== 'pending') return;
    this.polling = true;
    this.pollChecks = 0;
    this.pollUuid = this.link.uuid;
    this.pollProjectId = this.projectId;
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onVisibilityChange);
    // The first check is immediate: a snapshot that already rendered flips within one request.
    void this.pollOnce();
  }

  private stopPolling(): void {
    this.polling = false;
    this.waitingForVisible = false;
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
    this.pollAbort?.abort();
    this.pollAbort = null;
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private isPollingCurrentLink(): boolean {
    return this.polling && this.link?.uuid === this.pollUuid && this.projectId === this.pollProjectId
      && this.link?.snapshot?.status === 'pending';
  }

  /** One request at a time: the next check is scheduled only after the previous answer. */
  private schedulePoll(): void {
    if (this.pollChecks >= POLL_MAX_CHECKS) { this.stopPolling(); return; }
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      void this.pollOnce();
    }, POLL_INTERVAL_MS);
  }

  private async pollOnce(): Promise<void> {
    if (!this.polling || this.pollAbort) return;
    // Hidden time uses no checks: wait for visibilitychange to check again.
    if (typeof document !== 'undefined' && document.hidden) { this.waitingForVisible = true; return; }
    this.pollChecks++;
    const abort = new AbortController();
    this.pollAbort = abort;
    let fresh: MapFeatureLink;
    try {
      fresh = await this.manifestApi.link(this.pollProjectId, this.pollUuid, abort.signal);
    } catch {
      // 403 / 404 / offline: stop quietly and keep showing what we have.
      if (this.pollAbort === abort) this.stopPolling();
      return;
    }
    if (this.pollAbort !== abort) return;
    this.pollAbort = null;
    const current = this.link;
    // The host holds a moved pin that isn't saved yet: the api's copy is the old one, don't overwrite it.
    if (!fresh || !current || fresh.uuid !== current.uuid || !samePin(fresh, current)) { this.stopPolling(); return; }
    if (fresh.snapshot?.status === 'pending') { this.schedulePoll(); return; }
    this.stopPolling();
    const capture = refreshedCapture(current.capture, fresh.capture, this.captureBaseline);
    if (fresh.uuid === this.baselineLinkUuid) this.captureBaseline = captureUuid(fresh.capture);
    const updated = capture === fresh.capture ? fresh : { ...fresh, capture };
    this.link = updated;
    this.linkChange.emit(updated);
    void this.resolveBadge();
    this.cdr.markForCheck();
  }

  /**
   * A read-only card's snapshot tile opens the full picture in the app's viewer (as the location slot does for a
   * capture): the marked-up preview when there is markup, no attachment id, so the viewer never edits it.
   */
  viewSnapshot(event: Event): void {
    event.stopPropagation();
    const attachment = captureViewerAttachment(this.link?.snapshot as unknown as MapFeatureLinkCapture);
    if (attachment) this.appService.openCarousel(event, [attachment] as never[], attachment as never, false);
  }

  /** The revision the link was made on, even once superseded or withdrawn (spec §2), in the project's Drawings tab. */
  openSheet(event?: Event): void {
    event?.stopPropagation();
    if (!this.canOpenSheet) return;
    const sheet = this.link.sheet;
    // W70: already on that sheet (Add record's drawer, a record opened beside it): show the point there; navigating
    // would close the drawer and drop what is typed in it.
    if (this.sheetFocus.focus(sheet.revision_uuid, sheet.point ?? null)) return;
    const go = () => {
      // The record's right panel would otherwise stay open over the sheet.
      this.projectsService.closeRightPanel.next();
      void this.router.navigate(['/projects', this.projectId], { queryParams: { tab: 'drawings', revision: sheet.revision_uuid } });
    };
    const dirty = this.projectsService.isAnyFormDirty;
    if (!dirty?.getValue?.()) {
      go();
      return;
    }
    // Another sheet with unsaved changes in the record: the app's Unsaved changes confirm first
    const model = new ConfirmDialogModel('Unsaved changes', 'There are unsaved changes.<br/>Are you sure you want to discard?');
    this.dialog.open(ConfirmDialogComponent, { disableClose: true, data: model }).afterClosed().subscribe(discard => {
      if (discard) {
        dirty.next(false);
        go();
      }
    });
  }

  /**
   * W71: the markup editor on the link's revision and page, the record's point as the context pin (W51). A saved link
   * saves straight to its row; an unsaved one holds the strokes until the record saves (the picker's W51 path).
   */
  async openSheetMarkup(): Promise<void> {
    if (!this.canMarkUpSheet || this.showMarkupEditor) return;
    const link = this.link;
    const sheet = link.sheet;
    const page = await this.sheetPreviews.page(this.projectId, sheet.revision_uuid).catch(() => null);
    if (this.link !== link || !page?.originalUrl || !page.attachmentUuid) return;
    const held = link.uuid ? null : this.heldMarkup(link);
    this.markupConfig = {
      imageUrl: page.previewUrl, originalUrl: page.originalUrl, attachmentId: page.attachmentUuid, pageNumber: page.pageNumber,
      isDocument: true, visibility: 'shared',
      filename: `${page.sheetNumber ?? sheet.sheet_number ?? 'Sheet'} Rev ${page.label ?? sheet.label ?? ''}`.trim(),
      ...(link.uuid ? { linkUuid: link.uuid } : { deferSave: true, existingMarkup: held ? heldMarkup(held) : undefined }),
      recordKind: this.markupKind,
      contextPin: sheet.point ? { x: Number(sheet.point.x), y: Number(sheet.point.y) } : null,
      // Rule 5: with no pin, clearing the markup removes the location; the editor asks first
      confirmEmptySave: sheet.point ? null : PINLESS_EMPTY_CONFIRM,
    };
    this.sheetEditor = { revisionUuid: sheet.revision_uuid, attachmentUuid: page.attachmentUuid, pageNumber: page.pageNumber };
    this.showMarkupEditor = true;
    this.injector.get(AnalyticsService).capture('drawings.record_markup_opened', { source: 'card' });
    this.cdr.markForCheck();
  }

  private heldMarkup(link: MapFeatureLink): HeldSheetMarkup | null {
    try {
      return this.injector.get(SheetMarkupQueueService).heldFor(link);
    } catch {
      return null;
    }
  }

  /** W71: strokes saved from the card: held for an unsaved link, on the row for a saved one; the thumbnail follows */
  private onSheetMarkupSaved(result: MarkupSaveResult): void {
    const link = this.link;
    const target = this.sheetEditor;
    const config = this.markupConfig;
    this.sheetEditor = null;
    if (!link?.sheet || !target) { this.closeMarkupEditor(); return; }
    const markup = result.markup;
    const hasStrokes = !!markup?.annotation_data?.objects?.length;
    if (!link.sheet.point && !hasStrokes) {
      // Rule 5 (the editor asked): no pin and no markup is no drawing location, so the link goes
      if (config?.deferSave) this.injector.get(SheetMarkupQueueService).release(link.client_key);
      this.closeMarkupEditor();
      this.remove.emit();
      return;
    }
    if (config?.deferSave) {
      const queue = this.injector.get(SheetMarkupQueueService);
      if (hasStrokes) {
        // Review #1: held under the unsaved link's key, which the record's save sends and the api echoes back
        const key = link.client_key || newClientKey();
        queue.hold({
          key,
          revisionUuid: link.sheet.revision_uuid, point: link.sheet.point, attachmentUuid: target.attachmentUuid, pageNumber: target.pageNumber,
          sheetNumber: link.sheet.sheet_number, revisionLabel: link.sheet.label ?? null, projectId: this.projectId,
          body: { annotation_data: markup.annotation_data, canvas_width: markup.canvas_width, canvas_height: markup.canvas_height },
          previewDataUrl: result.previewDataUrl,
          kind: markup.kind === 'as_built' ? 'as_built' : null,
        });
        if (key !== link.client_key) {
          // The record's form keeps the key on its link, so its save sends it
          const keyed: MapFeatureLink = { ...link, client_key: key };
          this.link = keyed;
          this.linkChange.emit(keyed);
        }
      } else {
        queue.release(link.client_key);
      }
    } else {
      // On the link's row: its snapshot re-renders on the server; mark it so "Edit markup" shows meanwhile. Review M4: the
      // sheet's own flag follows too, so Remove link confirms exactly when strokes would go (and not after they were deleted)
      const updated: MapFeatureLink = {
        ...link, sheet: { ...link.sheet, has_markup: hasStrokes, ...savedMarkupKind(markup, hasStrokes) },
        snapshot: link.snapshot ? { ...link.snapshot, has_markup: hasStrokes } : link.snapshot,
      };
      this.link = updated;
      this.linkChange.emit(updated);
    }
    if (this.sheetPreview) {
      this.sheetPreview = {
        ...this.sheetPreview, markupSrc: hasStrokes ? result.previewDataUrl : null,
        strokes: hasStrokes ? markupBBox(markup.annotation_data, markup.canvas_width, markup.canvas_height) : null,
      };
    }
    this.closeMarkupEditor();
  }

  /** Spec §6 "optional markup via the existing editor", over the snapshot attachment. */
  openMarkup(): void {
    if (!this.canMarkUp || this.showMarkupEditor) return;
    const link = this.link;
    const snapshot = link.snapshot;
    // The clean original: preview_url carries burned-in strokes, re-marking it would draw them twice.
    this.openEditor(snapshot.attachment_uuid, snapshot.original_url || snapshot.preview_url || snapshot.thumb_url,
      `design-snapshot-${link.uuid}.png`);
  }

  private openEditor(attachmentId: string, imageUrl: string, filename: string): void {
    const link = this.link;
    const open = (existingMarkup: Markup | null) => {
      if (this.link !== link) return;
      this.markupConfig = { imageUrl, originalUrl: imageUrl, attachmentId, existingMarkup, pageNumber: 1, filename };
      this.showMarkupEditor = true;
      this.cdr.markForCheck();
    };
    // The attachment is keyed by uuid on the web; the markup api resolves uuid or id.
    this.markupService.getByAttachment(attachmentId, 1).subscribe({
      next: (markups: Markup[]) => open(markups?.length ? markups[0] : null),
      error: () => open(null),
    });
  }

  onMarkupSaved(result: MarkupSaveResult): void {
    if (this.sheetEditor) { this.onSheetMarkupSaved(result); return; }
    const link = this.link;
    const snapshot = link?.snapshot;
    if (!snapshot) { this.closeMarkupEditor(); return; }
    this.markupService.notifyMarkupSaved(snapshot.attachment_uuid, result.previewDataUrl);
    const updated: MapFeatureLink = {
      ...link,
      snapshot: {
        ...snapshot,
        thumb_url: result.updatedThumbUrl || result.previewDataUrl || snapshot.thumb_url,
        // The api's preview_url is the attachment's marked_up_url when there is one.
        preview_url: result.markedUpUrl || result.updatedPreviewUrl || snapshot.preview_url,
        // A save with every stroke cleared leaves an empty markup: not marked up.
        has_markup: !!result.markup?.annotation_data?.objects?.length,
      },
    };
    this.link = updated;
    this.linkChange.emit(updated);
    this.closeMarkupEditor();
  }

  closeMarkupEditor(): void {
    this.sheetEditor = null;
    this.showMarkupEditor = false;
    this.markupConfig = null;
    this.cdr.markForCheck();
  }
}

function captureUuid(capture: MapFeatureLinkCapture | null | undefined): string | null {
  return capture?.attachment_uuid ?? null;
}

/**
 * A finished render and the capture: a local capture that differs from what the server last sent (`baseline`) was
 * made, retaken or removed on this page, so it stays. Otherwise the server wins (another device, a server-side null),
 * except that the same capture keeps the local marked-up preview, which may be fresher than the server's.
 */
function refreshedCapture(local: MapFeatureLinkCapture | null | undefined, server: MapFeatureLinkCapture | null | undefined,
  baseline: string | null): MapFeatureLinkCapture | null | undefined {
  if (captureUuid(local) !== baseline) return local;
  if (local && server && local.attachment_uuid === server.attachment_uuid && local.preview_url !== server.preview_url) {
    return { ...server, preview_url: local.preview_url, thumb_url: local.thumb_url, has_markup: local.has_markup };
  }
  return server;
}

function samePin(a: MapFeatureLink, b: MapFeatureLink): boolean {
  return Number(a.pin?.latitude) === Number(b.pin?.latitude) && Number(a.pin?.longitude) === Number(b.pin?.longitude);
}
